import React from "react";
import { motion } from "framer-motion";

const ResourcesPage = () => {
  const upcomingFeatures = [
    {
      title: "Behind-the-Scenes Builds",
      desc: "Exclusive deep dives into how ContactHQ is crafted — from product decisions to real-world implementation secrets.",
      icon: "smart_display",
      color: "from-blue-500/10 to-transparent",
      iconColor: "text-blue-600 bg-blue-50 border-blue-100",
      glow: "hover:shadow-[0_15px_40px_-10px_rgba(59,130,246,0.3)]",
    },
    {
      title: "Hidden Developer Insights",
      desc: "The integration tricks, architecture decisions, and performance tweaks we rarely talk about — until now.",
      icon: "data_object",
      color: "from-[#E63946]/10 to-transparent",
      iconColor: "text-[#E63946] bg-[#E63946]/10 border-[#E63946]/20",
      glow: "hover:shadow-[0_15px_40px_-10px_rgba(230,57,70,0.3)]",
    },
    {
      title: "Growth Experiments Revealed",
      desc: "Real marketing experiments, conversion strategies, and lessons learned while building ContactHQ from the ground up.",
      icon: "campaign",
      color: "from-green-500/10 to-transparent",
      iconColor: "text-green-600 bg-green-50 border-green-100",
      glow: "hover:shadow-[0_15px_40px_-10px_rgba(34,197,94,0.3)]",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 70, damping: 15 },
    },
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] font-sans selection:bg-[#E63946] selection:text-white overflow-x-hidden relative flex flex-col justify-center py-24 sm:py-28">
      <motion.div
        animate={{ scale: [1, 1.1, 1], rotate: [0, 10, -10, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 right-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-[#E63946]/10 rounded-full blur-[120px] md:blur-[150px] translate-x-1/3 -translate-y-1/3 pointer-events-none"
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 w-[400px] h-[400px] md:w-[500px] md:h-[500px] bg-[#0B1C2D]/5 rounded-full blur-[100px] md:blur-[120px] -translate-x-1/3 translate-y-1/3 pointer-events-none"
      />

      <section className="mx-auto w-[95%] md:w-[90%] relative z-10 flex flex-col items-center">
        <div className="text-center mb-10 sm:mb-16 md:mb-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-white shadow-sm border border-slate-200 mb-6 sm:mb-8 cursor-default"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
              </span>
              <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.25em] text-[#0B1C2D]">
                Something Big Is Coming
              </span>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-[#0B1C2D] tracking-tighter leading-[1.1] mb-5 sm:mb-6 md:mb-8">
              We’re Building <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0B1C2D] via-[#E63946] to-[#E63946] bg-[length:200%_auto] animate-gradient">
                More Than Just Features.
              </span>
            </h1>

            <p className="text-slate-500 text-sm md:text-lg lg:text-xl font-medium max-w-3xl mx-auto leading-relaxed px-4">
              Behind every simple tool is a story, a struggle, and countless
              experiments. We’re preparing something deeper — a space where we
              share the real journey, the lessons, and the secrets behind
              building ContactHQ.
            </p>
          </motion.div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full mb-20 md:mb-28"
        >
          {upcomingFeatures.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -12, scale: 1.02 }}
              className={`bg-white p-8 md:p-10 rounded-[32px] border border-slate-100 shadow-sm relative overflow-hidden group transition-all duration-300 ${feature.glow}`}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out`}
              ></div>

              <div className="relative z-10">
                <div
                  className={`size-16 md:size-20 rounded-2xl flex items-center justify-center mb-8 shadow-sm border transition-transform duration-500 group-hover:rotate-[15deg] group-hover:scale-110 ${feature.iconColor}`}
                >
                  <span className="material-symbols-outlined text-3xl md:text-4xl">
                    {feature.icon}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-black text-[#0B1C2D] mb-4 group-hover:text-[#E63946] transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-slate-500 font-medium leading-relaxed">
                  {feature.desc}
                </p>
              </div>

              <div className="absolute top-8 right-8 opacity-20 group-hover:opacity-100 transition-opacity duration-300">
                <span className="material-symbols-outlined text-slate-400 text-4xl group-hover:animate-pulse">
                  lock
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, type: "spring", bounce: 0.3 }}
          className="bg-[#0B1C2D] rounded-[40px] md:rounded-[48px] h-64 md:h-80 w-full max-w-5xl relative overflow-hidden shadow-2xl border border-slate-800 flex items-center justify-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute -top-[50%] -right-[20%] w-[400px] h-[400px] md:w-[600px] md:h-[600px] border border-white/5 rounded-full border-dashed pointer-events-none"
          />

          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 md:w-80 md:h-80 bg-[#E63946] rounded-full blur-[80px] md:blur-[120px] opacity-40 pointer-events-none"></div>

          <div className="relative z-10 flex flex-col items-center">
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="size-20 md:size-24 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl mb-6"
            >
              <span className="material-symbols-outlined text-[#E63946] text-4xl md:text-5xl drop-shadow-[0_0_15px_rgba(230,57,70,0.8)]">
                auto_awesome
              </span>
            </motion.div>
            <h2 className="text-2xl md:text-4xl font-black text-white tracking-tighter text-center px-4">
              The next chapter of ContactHQ is unfolding.
            </h2>
          </div>

          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4wNSkiLz48L3N2Zz4=')] opacity-50 pointer-events-none"></div>
        </motion.div>
      </section>
    </div>
  );
};

export default ResourcesPage;
